import React, { createContext, useContext, useState, useEffect } from 'react';
import { clearLoggedOutDraft } from '../../../shared/hooks/useTasks';
import { trackEvent } from '../../../shared/utils/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children, forceLoggedOut = false }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Start as logged out until auth check completes
  const [userEmail, setUserEmail] = useState(null);
  const [userRegisteredAt, setUserRegisteredAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const applyRegisteredAtFromUserData = (userData) => {
    const registeredAt = userData?.registered != null ? Number(userData.registered) : null;
    setUserRegisteredAt(
      Number.isFinite(registeredAt) && registeredAt > 0 ? registeredAt : null
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const selector = params.get('selector');
    const token = params.get('token');
    const email = params.get('email');

    if (action === 'magic_verify' && selector && token && email) {
      (async () => {
        try {
          const url = `/api/auth.php?action=verify_magic_link&selector=${encodeURIComponent(selector)}&token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}`;
          const response = await fetch(url, { credentials: 'include' });
          const text = await response.text();
          let data = {};
          try {
            data = text ? JSON.parse(text) : {};
          } catch (_) {}
          if (response.ok && data.success) {
            trackEvent('login_success');
            window.location.replace('/');
            return;
          }
        } catch (e) {
          console.error('Magic link verify failed:', e);
        }
        setIsLoading(false);
      })();
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch('/api/user.php');
        if (response.ok) {
          const data = await response.json();
          setUserEmail(data.email);
          applyRegisteredAtFromUserData(data);
          setIsLoggedIn(true);
        } else if (response.status === 401 || response.status === 404) {
          setIsLoggedIn(false);
          setUserEmail(null);
          setUserRegisteredAt(null);
        }
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsLoggedIn(false);
        setUserEmail(null);
        setUserRegisteredAt(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'login',
          email,
          password,
          remember_me: rememberMe
        })
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        return { success: false, error: 'Server returned invalid response. Please try again.' };
      }
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsLoggedIn(true);
        setUserEmail(data.user.email);
        applyRegisteredAtFromUserData(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Error logging in:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const requestMagicLink = async (email) => {
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'request_magic_link',
          email
        })
      });
      
      const text = await response.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch (_) {
        return { success: false, error: 'Server returned invalid response. Please try again.' };
      }
      
      if (response.ok && data.success) {
        return { success: true, message: data.message };
      } else {
        return { success: false, error: data.error || 'Failed to send sign-in link' };
      }
    } catch (error) {
      console.error('Error requesting magic link:', error);
      return { success: false, error: error.message || 'Failed to send sign-in link' };
    }
  };

  const signup = async (email, inviteCode = '') => {
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'signup',
          email,
          invite_code: inviteCode
        })
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        return { success: false, error: 'Server returned invalid response. Please try again.' };
      }
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsLoggedIn(true);
        setUserEmail(data.user.email);
        applyRegisteredAtFromUserData(data.user);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Signup failed' };
      }
    } catch (error) {
      console.error('Error signing up:', error);
      return { success: false, error: error.message || 'Signup failed' };
    }
  };

  const joinWaitlist = async (email) => {
    try {
      const response = await fetch('/api/auth.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'join_waitlist',
          email
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        return { success: false, error: 'Server returned invalid response. Please try again.' };
      }

      const data = await response.json();
      if (response.ok && data.success) {
        return { success: true };
      }
      return { success: false, error: data.error || 'Failed to join waitlist' };
    } catch (error) {
      console.error('Error joining waitlist:', error);
      return { success: false, error: error.message || 'Failed to join waitlist' };
    }
  };

  const logout = async () => {
    try {
      const response = await fetch('/api/logout.php', {
        method: 'POST',
        credentials: 'include' // Include cookies for session
      });
      if (response.ok) {
        clearLoggedOutDraft();
        window.location.reload();
      }
    } catch (error) {
      console.error('Error logging out:', error);
      clearLoggedOutDraft();
      window.location.reload();
    }
  };

  const effectiveIsLoggedIn = forceLoggedOut ? false : isLoggedIn;

  const value = {
    isLoggedIn: effectiveIsLoggedIn,
    userEmail,
    userRegisteredAt,
    isLoading,
    logout,
    login,
    requestMagicLink,
    signup,
    joinWaitlist,
    setUserEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

